import React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import MP4CampaignForm from "./MP4CampaignForm";
import VASTCampaignForm from "./VASTCampaignForm";
import OfferAPI from "../../api/OfferAPI";
import "../../css/admin/advertisings.scss";
import { AdvertisingEnum } from "../../types";
import Breadcrumbs from "../utils/Breadcrumbs";
import InfoPanel from "../utils/InfoPanel";
import Spinner from "../utils/Spinner";

import type { JSX } from "react";

import type { IAdvertisingSingle } from "../../types";

function AdvertisingsPage(): JSX.Element {
  const { t } = useTranslation("editor-advertisings");
  const { id, projectSlug } = useParams<{ id: string; projectSlug: string }>();
  const queryClient = useQueryClient();

  const getSingleAdvertisingQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getSingleAdvertising,
    params: [
      {
        uuid: id,
        projectSlug,
      },
    ],
  });

  const { isLoading, data: getCampaignData } = useQuery({
    ...getSingleAdvertisingQuery,
    enabled: Boolean(id),
  });

  const updateAdvertising = useMutation({
    mutationFn: async (advertising: IAdvertisingSingle) =>
      OfferAPI.updateAdvertising({
        item: advertising,
        projectSlug,
      }),
    onSuccess: () => {
      toast.success(t("editor-notif:saveSuccess"));
      queryClient.invalidateQueries(getSingleAdvertisingQuery.queryKey);
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const itemIsValide = (advertising: IAdvertisingSingle): unknown[] => {
    const errorMessages = [];

    if (!advertising.label || advertising.label.length <= 0) {
      errorMessages.push(t("editor-errors:noLabel"));
    }

    return errorMessages;
  };

  const onUpdate = (
    advertising: IAdvertisingSingle,
    onSuccess?: () => void,
  ): void => {
    const errorMessages = itemIsValide(advertising);

    if (errorMessages.length === 0) {
      updateAdvertising.mutate(advertising, { onSuccess });
    } else {
      toast.error(`${t("editor-notif:saveError")} ${errorMessages}`);
    }
  };

  const renderCampaignForm = (): JSX.Element | null => {
    switch (getCampaignData?.type) {
      case AdvertisingEnum.CSAI_VAST:
        return (
          <VASTCampaignForm campaign={getCampaignData} onSubmit={onUpdate} />
        );
      case AdvertisingEnum.CSAI_MP4:
        return (
          <MP4CampaignForm campaign={getCampaignData} onSubmit={onUpdate} />
        );
      default:
        return null;
    }
  };

  const navigationHeaderLinks = {
    en: "https://support.okast.tv/en/support/solutions/articles/103000101744-create-a-sponsorship-or-a-self-promotion-campaign",
    fr: "https://support.okast.tv/fr/support/solutions/articles/103000101744-cr%C3%A9er-une-campagne-de-sponsoring-ou-d-auto-promo",
  };

  const breadcrumbs = [
    {
      name: t("sectionTitle"),
      url: "/advertisings",
    },
    {
      name: t("edit"),
    },
  ];

  return (
    <div className="campaign-form-container">
      <div className="editor-form-nav">
        <div className="editor-form-nav-left">
          <Breadcrumbs items={breadcrumbs} />
          <InfoPanel links={navigationHeaderLinks} />
        </div>
      </div>

      {isLoading ? (
        <div className="admin-user-form_spinner-container">
          <Spinner />
        </div>
      ) : (
        renderCampaignForm()
      )}
    </div>
  );
}

export default AdvertisingsPage;
